import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import { AuthLayout } from '@/components/shell/AuthLayout';
import { Alert } from '@/components/ui/feedback';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Inputs';
import { api, isApiError } from '@/lib/api/client';
import { useFocusOnMount } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';

/** /forgot-password and /staff/forgot-password: always answers the same way so accounts cannot be probed. */
export function ForgotPasswordPage({ staff = false }: { staff?: boolean }) {
  const { t } = useI18n();
  const loginRef = useFocusOnMount<HTMLInputElement>();
  const [identifier, setIdentifier] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!identifier.trim()) {
      setError(t('error.fieldRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('auth/forgot-password', { login: identifier.trim() }, { anonymous: true });
      setSent(true);
    } catch (e) {
      // 404/400 are reported as "sent" too; only real outages surface.
      if (isApiError(e) && e.status < 500 && e.status !== 429) {
        setSent(true);
      } else {
        setError(isApiError(e) ? e.message : t('error.generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  const back = staff ? '/staff/login' : '/login';
  return (
    <AuthLayout
      audience={staff ? 'staff' : 'patron'}
      title={t('auth.resetTitle')}
      subtitle="Enter your card number, user ID or email and we will send a reset link."
      footer={
        <Link to={back} className="font-medium text-sapphire-700 hover:underline dark:text-sapphire-300">
          {t('nav.signIn')}
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <CheckCircle2 className="h-10 w-10 text-success-600" aria-hidden="true" />
          <p role="status" className="text-sm text-ink">
            {t('auth.resetSent')}
          </p>
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-5">
          {error && (
            <Alert tone="danger" dense>
              {error}
            </Alert>
          )}
          <Field label={t('auth.login')} required>
            <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" ref={loginRef} size="lg" />
          </Field>
          <Button type="submit" size="lg" fullWidth loading={busy}>
            {t('auth.resetSend')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

/** /reset-password?token=... (linked from the PASSWORD_RESET notice). */
export function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && password !== confirm;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordsMatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('auth/reset-password', { token, password }, { anonymous: true });
      setDone(true);
    } catch (e) {
      setError(isApiError(e) ? e.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('auth.resetTitle')} subtitle={token ? undefined : 'This reset link is missing its token. Request a new one.'}>
      {done ? (
        <div className="flex flex-col items-center gap-4 py-2 text-center">
          <CheckCircle2 className="h-10 w-10 text-success-600" aria-hidden="true" />
          <p role="status" className="text-sm text-ink">
            Your password has been changed.
          </p>
          <ButtonLink to="/login">{t('nav.signIn')}</ButtonLink>
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-5">
          {error && (
            <Alert tone="danger" dense>
              {error}
            </Alert>
          )}
          <Field label={t('auth.newPassword')} hint="At least 8 characters." required>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" size="lg" disabled={!token} />
          </Field>
          <Field label={t('auth.confirmPassword')} error={mismatch ? t('auth.passwordsMatch') : undefined} required>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" size="lg" disabled={!token} />
          </Field>
          <Button type="submit" size="lg" fullWidth loading={busy} disabled={!token}>
            {t('common.save')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
